/**
 * Pure orbit kinematics and the arithmetic that decides whether a path can
 * be collided against accurately.
 *
 * Deliberately owns **no** state and touches **no** instance. The attack
 * instances an orbit drives stay in `AttackInstancePool`, the damage stays
 * in the existing resolver, and this module only answers two questions:
 * where is an orbiting member at time `t`, and how finely must its arc be
 * subdivided before a straight sweep across it is honest?
 *
 * Nothing here opens a capability; `ORBIT_MOTION` and its siblings are
 * the registry's (`composite-capability.ts`).
 *
 * Design: `docs/superpowers/specs/2026-09-19-orbit-motion-and-swept-collision.md`.
 */
import type { Vec2 } from './state.js';

/**
 * Where an orbit turns around.
 *
 * `FOLLOW_CASTER` reads the caster's position **live**, not from the
 * activation snapshot: an orbit frozen at the caster's cast-time position
 * is a `FIXED_POINT` orbit with extra steps. Only the centre is live —
 * damage, source and mastery still come from the activation snapshot like
 * every other stage.
 *
 * `FOLLOW_TARGET` is deliberately absent. Tracking an enemy is target
 * seeking, which belongs to C, and leaving it unrepresentable is stronger
 * than leaving it discouraged.
 */
export type OrbitCentrePolicy =
  | { readonly kind: 'FOLLOW_CASTER' }
  | { readonly kind: 'FIXED_POINT'; readonly point: Vec2 };

export interface OrbitSpec {
  readonly radiusM: number;
  /** Radians per second. Sign gives the direction of travel. */
  readonly angularSpeedRadPerS: number;
  readonly centre: OrbitCentrePolicy;
}

/** Where this member sits at cast-relative time `t`, given its centre now. */
export function orbitPosition(
  spec: Pick<OrbitSpec, 'radiusM' | 'angularSpeedRadPerS'>,
  centre: Vec2,
  initialPhaseRad: number,
  tSeconds: number,
): Vec2 {
  const angle = initialPhaseRad + spec.angularSpeedRadPerS * tSeconds;
  return { x: centre.x + spec.radiusM * Math.cos(angle), y: centre.y + spec.radiusM * Math.sin(angle) };
}

/**
 * The even starting phases of `count` members, matching what a `RING`
 * formation already produces so an orbit is `RING` plus an angular speed
 * rather than a second placement convention.
 */
export function orbitInitialPhases(count: number, initialAngleRad = 0): readonly number[] {
  if (!Number.isInteger(count) || count < 1) return [];
  const step = (2 * Math.PI) / count;
  return Array.from({ length: count }, (_unused, i) => initialAngleRad + step * i);
}

/**
 * Worst-case distance between the straight chord joining two orbit samples
 * and the real arc between them:
 *
 * ```text
 * ε = r · ( 1 − cos(Δθ / 2) )
 * ```
 *
 * This is the whole reason a plain swept-circle test is not enough for an
 * orbit. The chord cuts *inside* the arc, so a target sitting in the gap is
 * missed entirely, and the gap widens with angular speed. Every tolerance
 * discussion has to be stated against this number.
 */
export function chordDeviationM(radiusM: number, sweptAngleRad: number): number {
  const swept = Math.abs(sweptAngleRad);
  // The raw formula rises on [0, 2π], peaks at 2r when Δθ = 2π, and then
  // FALLS again — at 4π it returns zero, which would report two full laps
  // as no deviation at all. So it is exact up to a full turn and clamped
  // to the peak beyond it, which keeps the bound monotone in speed. Every
  // subdivision decision depends on that monotonicity.
  //
  // Clamping earlier (at π, as this first did) is safe but wasteful: the
  // true deviation at π is r, not 2r, so a half-turn tick was subdivided
  // as if it were a full one. The cost of that over-conservatism is
  // measured in the P8-4 harness rather than argued about.
  if (swept >= 2 * Math.PI) return 2 * radiusM;
  return radiusM * (1 - Math.cos(swept / 2));
}

/**
 * The total angle this orbit turns through in one tick, **unnormalised**.
 *
 * Load-bearing: at high angular speed a tick can span a full revolution or
 * more, and then the start and end positions coincide. A straight sweep
 * between them sees an orb that did not move, and everything it passed
 * through is missed. Subdivision must therefore be decided from the total
 * rotation, never from the angle difference after wrapping to (-π, π].
 */
export function sweptAngleRad(angularSpeedRadPerS: number, dtSeconds: number): number {
  return angularSpeedRadPerS * dtSeconds;
}

/**
 * The largest angle one straight sub-step may span while keeping the chord
 * within `toleranceM` of the arc — the inverse of `chordDeviationM`.
 *
 * Returns `undefined` when the tolerance is large enough that no
 * subdivision is needed at all (`≥ 2r`, i.e. the chord can never deviate
 * that far), so a caller can skip subdivision rather than compute a
 * meaningless bound.
 */
export function maxStepAngleRad(radiusM: number, toleranceM: number): number | undefined {
  if (!(radiusM > 0) || !(toleranceM > 0)) return 0;
  const cosHalf = 1 - toleranceM / radiusM;
  if (cosHalf <= -1) return undefined;
  return 2 * Math.acos(Math.min(1, cosHalf));
}

export type SubdivisionPlan =
  | { readonly ok: true; readonly steps: number; readonly stepAngleRad: number; readonly deviationM: number }
  | { readonly ok: false; readonly code: string; readonly message: string; readonly requiredSteps: number };

/**
 * How many straight sub-steps this tick's arc needs, or a refusal.
 *
 * **The refusal is the point.** When the required subdivision exceeds the
 * approved computation ceiling, the honest answer is that this
 * orbit/tick-rate combination cannot be collided against accurately — so
 * the capability is refused for it. Silently capping the step count and
 * accepting the resulting misses would be a fast orbit passing through
 * enemies, and silently widening the tolerance would be the same thing
 * with a nicer number attached.
 *
 * `toleranceM` and `maxSteps` are **approved** inputs. This module does not
 * carry defaults for either, because choosing them is a technical-profile
 * owner decision informed by measurement, not a convenience.
 */
export function planArcSubdivision(
  radiusM: number,
  sweptAngleRad: number,
  toleranceM: number,
  maxSteps: number,
): SubdivisionPlan {
  const swept = Math.abs(sweptAngleRad);
  if (!Number.isFinite(radiusM) || radiusM <= 0) {
    return { ok: false, code: 'ORBIT_RADIUS_INVALID', message: 'orbit radius must be finite and positive', requiredSteps: 0 };
  }
  if (!Number.isFinite(swept)) {
    return { ok: false, code: 'ORBIT_SWEEP_INVALID', message: 'swept angle must be finite', requiredSteps: 0 };
  }
  if (swept === 0) return { ok: true, steps: 1, stepAngleRad: 0, deviationM: 0 };

  const limit = maxStepAngleRad(radiusM, toleranceM);
  if (limit === undefined) return { ok: true, steps: 1, stepAngleRad: swept, deviationM: chordDeviationM(radiusM, swept) };
  if (!(limit > 0)) {
    return {
      ok: false,
      code: 'ORBIT_CONTACT_TOLERANCE_UNREACHABLE',
      message: 'the requested tolerance admits no finite step angle',
      requiredSteps: Number.POSITIVE_INFINITY,
    };
  }

  const steps = Math.max(1, Math.ceil(swept / limit));
  if (steps > maxSteps) {
    return {
      ok: false,
      code: 'ORBIT_CONTACT_PRECISION_BUDGET_EXCEEDED',
      message:
        `this orbit sweeps ${swept.toFixed(4)} rad in one step and needs ${steps} sub-steps to stay within ` +
        `${toleranceM}m of its arc, above the approved ceiling of ${maxSteps}. Collision cannot be resolved ` +
        'accurately for this combination, so it is refused rather than approximated',
      requiredSteps: steps,
    };
  }

  const stepAngle = swept / steps;
  return { ok: true, steps, stepAngleRad: stepAngle, deviationM: chordDeviationM(radiusM, stepAngle) };
}

/**
 * The sample points a tick's arc is collided along, in travel order.
 *
 * Emits `steps + 1` points so the caller has `steps` straight segments to
 * hand to the existing swept-circle test — the orbit reuses that resolver
 * rather than introducing a second contact model.
 */
export function arcSamplePoints(
  spec: Pick<OrbitSpec, 'radiusM' | 'angularSpeedRadPerS'>,
  centreAtStart: Vec2,
  centreAtEnd: Vec2,
  initialPhaseRad: number,
  tStartSeconds: number,
  tEndSeconds: number,
  steps: number,
): readonly Vec2[] {
  const count = Math.max(1, Math.floor(steps));
  return Array.from({ length: count + 1 }, (_unused, i) => {
    const f = i / count;
    const t = tStartSeconds + (tEndSeconds - tStartSeconds) * f;
    // A moving centre is interpolated linearly across the tick, which is
    // what the caster's own movement already is between two samples. It is
    // stated here rather than assumed, because it is an approximation the
    // fixed-centre case does not need and the tolerance above does not
    // cover.
    const centre = {
      x: centreAtStart.x + (centreAtEnd.x - centreAtStart.x) * f,
      y: centreAtStart.y + (centreAtEnd.y - centreAtStart.y) * f,
    };
    return orbitPosition(spec, centre, initialPhaseRad, t);
  });
}

/**
 * Extra path length the CENTRE contributes over one tick.
 *
 * `chordDeviationM` bounds how far a straight sub-step strays from a
 * circular arc **about a fixed centre**. Under `FOLLOW_CASTER` the centre
 * itself travels, so the true path is a trochoid rather than an arc and
 * that bound alone does not cover it. The centre's own displacement is
 * added here so a subdivision decision accounts for both, rather than
 * silently assuming the caster stood still.
 *
 * Linear interpolation of the centre across the tick is itself an
 * approximation of the caster's real movement; it is exact only where the
 * caster moved in a straight line during the tick, which is what a
 * per-tick position sample can tell us anyway.
 */
export function centreDisplacementM(centreAtStart: Vec2, centreAtEnd: Vec2): number {
  return Math.hypot(centreAtEnd.x - centreAtStart.x, centreAtEnd.y - centreAtStart.y);
}

/**
 * Subdivision for a tick, accounting for BOTH the arc and a moving centre.
 *
 * The arc term is `planArcSubdivision`. The centre term requires enough
 * sub-steps that the centre moves no further than the tolerance between
 * samples, because a target can be swept over by the centre's travel just
 * as it can by the orbit's. The stricter of the two wins.
 */
export function planOrbitTickSubdivision(
  radiusM: number,
  sweptAngle: number,
  centreAtStart: Vec2,
  centreAtEnd: Vec2,
  toleranceM: number,
  maxSteps: number,
): SubdivisionPlan {
  const arc = planArcSubdivision(radiusM, sweptAngle, toleranceM, maxSteps);
  if (!arc.ok) return arc;

  const drift = centreDisplacementM(centreAtStart, centreAtEnd);
  const centreSteps = toleranceM > 0 ? Math.ceil(drift / toleranceM) : 0;
  const steps = Math.max(arc.steps, centreSteps, 1);

  if (steps > maxSteps) {
    return {
      ok: false,
      code: 'ORBIT_CONTACT_PRECISION_BUDGET_EXCEEDED',
      message:
        `this orbit needs ${steps} sub-steps to stay within ${toleranceM}m of both its arc and its ` +
        `moving centre (which travelled ${drift.toFixed(4)}m this tick), above the approved ceiling of ` +
        `${maxSteps}. Collision cannot be resolved accurately for this combination, so it is refused`,
      requiredSteps: steps,
    };
  }

  const stepAngle = sweptAngle / steps;
  return { ok: true, steps, stepAngleRad: stepAngle, deviationM: chordDeviationM(radiusM, stepAngle) };
}

/**
 * The part of every tick's subdivision that the authored orbit and the tick
 * length alone decide: its arc, with the centre held still.
 *
 * It is `planOrbitTickSubdivision` with no centre drift, not a second
 * formula, so a verdict here is exactly the verdict `advanceOrbits` would
 * reach on a tick where the centre did not move. If this refuses, every
 * such tick refuses — and a refused tick commits nothing, lifetime
 * included, so the orb would never age out. That is why the compiler (at
 * the profile's reference tick) and `AuthoritativeWorld.beginCast` (at its
 * own tick) ask this before anything spawns, rather than letting the
 * per-tick refusal discover it.
 *
 * A moving centre adds to this and can still refuse an individual tick (a
 * caster displaced far within one tick); that refusal is transient, since
 * the next tick's drift is its own.
 */
export function planOrbitArcFeasibility(
  radiusM: number,
  angularSpeedRadPerS: number,
  dtSeconds: number,
  toleranceM: number,
  maxSteps: number,
): SubdivisionPlan {
  const still: Vec2 = { x: 0, y: 0 };
  return planOrbitTickSubdivision(radiusM, sweptAngleRad(angularSpeedRadPerS, dtSeconds), still, still, toleranceM, maxSteps);
}
